import { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Pill, Loading, Err, tableStyles, EmptyState } from "./_shared";
import { Workflow, Cpu, GitBranch, Timer } from "lucide-react";

export default function Automations() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/api/admin/automations").then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  return (
    <div data-testid="admin-automations">
      <PageHead title="Automations" sub="Workflow runs across workspaces" right={<Pill tone="warn">Read-only · Turn D</Pill>} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card title={`Workflows · ${data.workflows?.length ?? 0}`}>
          {(!data.workflows || data.workflows.length === 0) ? (
            <EmptyState title="No workflows yet" description="Workspace automations will appear here. CRUD and trigger management ships in Turn D." />
          ) : (
            <table style={tableStyles.table}>
              <thead><tr><th style={tableStyles.th}>Name</th><th style={tableStyles.th}>Workspace</th><th style={tableStyles.th}>Status</th></tr></thead>
              <tbody>
                {data.workflows.map((w) => (
                  <tr key={w.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={{ ...tableStyles.td, fontWeight: 700 }}>{w.name}</td>
                    <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{(w.workspace_id || "").slice(0, 12)}</td>
                    <td style={tableStyles.td}><Pill tone={w.enabled ? "success" : "muted"}>{w.enabled ? "live" : "paused"}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title={`Recent runs · ${data.runs?.length ?? 0}`}>
          {(!data.runs || data.runs.length === 0) ? (
            <EmptyState title="No automation runs yet" />
          ) : (
            <table style={tableStyles.table}>
              <thead><tr><th style={tableStyles.th}>When</th><th style={tableStyles.th}>Workflow</th><th style={tableStyles.th}>Status</th></tr></thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                    <td style={{ ...tableStyles.td, fontSize: 11 }}>{(r.created_at || "").slice(0, 19)}</td>
                    <td style={tableStyles.td}>{r.workflow_id}</td>
                    <td style={tableStyles.td}><Pill tone={r.status === "succeeded" ? "success" : r.status === "failed" ? "danger" : "warn"}>{r.status || "—"}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="What ships in Turn D" style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <Tile icon={<Workflow size={14} />} label="Visual workflow builder" />
            <Tile icon={<Cpu size={14} />} label="AI step nodes (gen, transform, classify)" />
            <Tile icon={<GitBranch size={14} />} label="Conditional branches & retries" />
            <Tile icon={<Timer size={14} />} label="Cron + webhook + manual triggers" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Tile({ icon, label }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 14px",
      borderRadius: 10, background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)" }}>
      <span style={{ color: "var(--pl-aqua)" }}>{icon}</span>
      <div style={{ fontSize: 12.5, fontWeight: 700 }}>{label}</div>
    </div>
  );
}
